import React, { useEffect, useMemo, useState } from 'react';import api from '../../services/api';
import { SMART_COLORS as COLORS } from '../../theme/smartStableTheme';
import {
  Container,
  Typography,
  Box,
  Grid,
  TextField,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import Autocomplete from '@mui/material/Autocomplete';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

const apiUrl = process.env.REACT_APP_API_URL;

function normalizeArabic(str = '') {
  // إزالة التشكيل وتوحيد بعض أشكال الألف/الياء/التاء المربوطة
  return String(str)
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u064B-\u0652]/g, '') // التشكيل
    .replace(/[آأإ]/g, 'ا')
    .replace(/ى/g, 'ي')
    .replace(/ة/g, 'ه')
    .trim();
}

function safe(str) {
  return String(str || '').trim();
}

// محاولات مرنة لقراءة أسماء الحصان
function getHorseNameAr(h) {
  return (
    h.horse_name_ar || h.horseNameAr || h.name_ar || h.ar_name || '' // قد لا يتوفر العربي
  );
}
function getHorseNameEn(h) {
  return (
    h.horse_name_en ||
    h.horse_name || // أغلب مشاريعك تستخدم هذا كإنجليزي
    h.horseName ||
    h.name_en ||
    h.name ||
    ''
  );
}
function getOwnerName(h) {
  return h.owner_name || h.ownerName || h.client_name || '';
}

function buildHorseLabel(h) {
  const ar = safe(getHorseNameAr(h));
  const en = safe(getHorseNameEn(h));
  const owner = safe(getOwnerName(h));
  const ownerTrail = owner ? ` - ${owner}` : '';
  if (ar && en) return `${ar} (${en})${ownerTrail}`;
  return `${ar || en}${ownerTrail}`;
}

function driverLabel(option) {
  if (!option) return '';
  return typeof option === 'string' ? option : option.name || option.driver_name || '';
}

const HorseTransportForm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [horses, setHorses] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [driverDialogOpen, setDriverDialogOpen] = useState(false);
  const [driverForm, setDriverForm] = useState({ name: '', phone: '' });
  const [form, setForm] = useState({
    driver_id: '',
    driver_name: '',
    driver_phone: '',
    from_location: '',
    to_location: '',
    horse_condition: '',
    notes: '',
    total_cost: '',
    profit_margin: '',
    selectedHorses: [], // [{ horse_id, client_id, owner_name }]
  });

  // collator لفرز عربي/إنجليزي
  const collator = useMemo(
    () =>
      new Intl.Collator(['ar', 'en'], { sensitivity: 'base', numeric: true }),
    []
  );

  // جلب الخيول
  useEffect(() => {
    (async () => {
      const [horsesRes, driversRes] = await Promise.all([
        api.get(`${apiUrl}/horses/inside`),
        api.get(`${apiUrl}/transport/drivers`),
      ]);
      setHorses(horsesRes.data || []);
      setDrivers(driversRes.data || []);
    })();
  }, []);

  // وضع التعديل: تحميل السجلّ القائم وتعبئة النموذج.
  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const res = await api.get(`${apiUrl}/transport/${id}`);
        const d = res.data || {};
        setForm({
          driver_id: '',
          driver_name: d.driver_name || '',
          driver_phone: d.driver_phone || '',
          from_location: d.from_location || '',
          to_location: d.to_location || '',
          horse_condition: d.horse_condition || '',
          notes: d.notes || '',
          total_cost: d.total_cost ?? '',
          profit_margin: d.profit_margin ?? '',
          selectedHorses: Array.isArray(d.horses)
            ? d.horses.map((h) => ({
                horse_id: h.horse_id,
                client_id: h.client_id,
                owner_name: h.owner_name || '',
              }))
            : [],
        });
      } catch (e) {
        console.error(e);
        alert('❌ تعذّر تحميل سجلّ النقل');
      }
    })();
  }, [isEdit, id]);

  // قائمة مرتبة أبجديًا
  const sortedHorses = useMemo(() => {
    const copy = [...horses];
    copy.sort((a, b) =>
      collator.compare(buildHorseLabel(a), buildHorseLabel(b))
    );
    return copy;
  }, [horses, collator]);

  const selectedDriver = useMemo(() => {
    if (form.driver_id) {
      return drivers.find((driver) => Number(driver.id) === Number(form.driver_id)) || null;
    }
    if (form.driver_name) {
      return drivers.find((driver) => driver.name === form.driver_name) || null;
    }
    return null;
  }, [drivers, form.driver_id, form.driver_name]);

  const handleHorseChange = (index, key, value) => {
    setForm((prev) => {
      const updated = [...prev.selectedHorses];
      updated[index] = { ...updated[index], [key]: value };
      return { ...prev, selectedHorses: updated };
    });
  };

  const addHorse = () => {
    setForm((prev) => ({
      ...prev,
      selectedHorses: [
        ...prev.selectedHorses,
        { horse_id: '', client_id: '', owner_name: '' },
      ],
    }));
  };

  const saveDriver = async () => {
    const name = safe(driverForm.name);
    if (!name) {
      alert(t('horse_transport.driver_required') || 'اسم السائق مطلوب');
      return;
    }

    try {
      const res = await api.post(`${apiUrl}/transport/drivers`, {
        name,
        phone: safe(driverForm.phone),
      });
      const created = res.data;
      setDrivers((prev) => {
        const next = prev.filter((driver) => Number(driver.id) !== Number(created.id));
        return [...next, created].sort((a, b) => collator.compare(driverLabel(a), driverLabel(b)));
      });
      setForm((prev) => ({
        ...prev,
        driver_id: created.id,
        driver_name: created.name,
        driver_phone: created.phone || '',
      }));
      setDriverDialogOpen(false);
      setDriverForm({ name: '', phone: '' });
    } catch (error) {
      console.error(error);
      alert(t('horse_transport.driver_save_failed') || 'فشل حفظ السائق');
    }
  };

  const handleSubmit = async () => {
    try {
      const payload = {
        ...form,
        total_cost: parseFloat(form.total_cost || 0),
        profit_margin: parseFloat(form.profit_margin || 0),
        horses: form.selectedHorses,
      };
      if (isEdit) {
        await api.put(`${apiUrl}/transport/${id}`, payload);
      } else {
        await api.post(`${apiUrl}/transport`, payload);
      }
      alert('✅ تم الحفظ بنجاح');
      navigate('/HorseTransportReportPage');
    } catch (e) {
      console.error(e);
      alert('❌ حدث خطأ أثناء الحفظ');
    }
  };

  const costPerHorse = () => {
    const total = parseFloat(form.total_cost || 0);
    const margin = parseFloat(form.profit_margin || 0);
    const count = form.selectedHorses.length || 1;
    return ((total + margin) / count).toFixed(2);
  };

  return (
    <Container component="main" maxWidth="xs" variant="form">
      <Typography variant="h5" mb={4}>
        {isEdit ? t('horse_transport.edit_title') : t('horse_transport.title')}
      </Typography>

      <Grid container spacing={2}>
        {/* السائق */}
        <Grid item xs={12} md={7}>
          <Stack direction="row" spacing={1} alignItems="flex-start">
            <Autocomplete
              fullWidth
              options={drivers}
              value={selectedDriver}
              getOptionLabel={driverLabel}
              isOptionEqualToValue={(option, value) => Number(option?.id) === Number(value?.id)}
              onChange={(_, value) => {
                setForm((prev) => ({
                  ...prev,
                  driver_id: value?.id || '',
                  driver_name: value?.name || '',
                  driver_phone: value?.phone || '',
                }));
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('horse_transport.select_driver')}
                  placeholder={t('horse_transport.driver_search')}
                  fullWidth
                />
              )}
            />
            <IconButton
              onClick={() => setDriverDialogOpen(true)}
              sx={{
                mt: 0.75,
                width: 44,
                height: 44,
                border: '1px solid #eadfca',
                color: COLORS.gold,
                bgcolor: COLORS.card,
              }}
            >
              <AddIcon />
            </IconButton>
          </Stack>
        </Grid>
        <Grid item xs={12} md={5}>
          <TextField
            fullWidth
            label={t('horse_transport.driver_phone')}
            value={form.driver_phone}
            onChange={(e) => setForm({ ...form, driver_phone: e.target.value })}
          />
        </Grid>

        {/* من - إلى */}
        <Grid item xs={6}>
          <TextField
            fullWidth
            label={t('horse_transport.from_location')}
            value={form.from_location}
            onChange={(e) =>
              setForm({ ...form, from_location: e.target.value })
            }
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            fullWidth
            label={t('horse_transport.to_location')}
            value={form.to_location}
            onChange={(e) => setForm({ ...form, to_location: e.target.value })}
          />
        </Grid>

        {/* التكلفة والربح */}
        <Grid item xs={6}>
          <TextField
            fullWidth
            label={t('horse_transport.total_cost')}
            type="number"
            value={form.total_cost}
            onChange={(e) => setForm({ ...form, total_cost: e.target.value })}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            fullWidth
            label={t('horse_transport.profit_margin')}
            type="number"
            value={form.profit_margin}
            onChange={(e) =>
              setForm({ ...form, profit_margin: e.target.value })
            }
          />
        </Grid>

        {/* حالة الخيول */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            label={t('horse_transport.horse_condition')}
            value={form.horse_condition}
            onChange={(e) =>
              setForm({ ...form, horse_condition: e.target.value })
            }
          />
        </Grid>

        {/* الملاحظات */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            multiline
            rows={3}
            label={t('horse_transport.notes')}
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
          />
        </Grid>
      </Grid>

      {/* قائمة الخيول */}
      <Box mt={5}>
        <Typography variant="h6" gutterBottom>
          {t('horse_transport.horse_list')}
        </Typography>

        {form.selectedHorses.map((row, index) => {
          const valueObj =
            horses.find((h) => h.horse_id === row.horse_id) || null;

          return (
            <Grid container spacing={2} key={index} mt={1}>
              <Grid item xs={12}>
                <Autocomplete
                  options={sortedHorses}
                  value={valueObj}
                  // منع اختيار نفس الخيل في صف آخر
                  getOptionDisabled={(opt) =>
                    form.selectedHorses.some(
                      (sh, i) => i !== index && sh.horse_id === opt.horse_id
                    )
                  }
                  isOptionEqualToValue={(opt, val) =>
                    opt?.horse_id === val?.horse_id
                  }
                  getOptionLabel={buildHorseLabel}
                  noOptionsText={t('no_options') || 'لا توجد نتائج'}
                  // فلترة مخصصة: عربي/إنجليزي/اسم المالك
                  filterOptions={(options, { inputValue }) => {
                    const q = normalizeArabic(inputValue);
                    if (!q) return options;
                    return options.filter((o) => {
                      const ar = normalizeArabic(getHorseNameAr(o));
                      const en = normalizeArabic(getHorseNameEn(o));
                      const owner = normalizeArabic(getOwnerName(o));
                      return (
                        ar.includes(q) || en.includes(q) || owner.includes(q)
                      );
                    });
                  }}
                  onChange={(_, newValue) => {
                    if (!newValue) {
                      // مسح السطر
                      handleHorseChange(index, 'horse_id', '');
                      handleHorseChange(index, 'client_id', '');
                      handleHorseChange(index, 'owner_name', '');
                      return;
                    }
                    // تعبئة الحقول
                    handleHorseChange(index, 'horse_id', newValue.horse_id);
                    handleHorseChange(index, 'client_id', newValue.owner_id);
                    handleHorseChange(
                      index,
                      'owner_name',
                      getOwnerName(newValue)
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('horse_transport.select_horse')}
                      placeholder={t('horse_transport.searchHorseHint')}
                      fullWidth
                    />
                  )}
                />
              </Grid>

              <Grid item xs={6}>
                <TextField
                  fullWidth
                  disabled
                  label={t('horse_transport.select_owner')}
                  value={row.owner_name || ''}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  disabled
                  label={t('horse_transport.cost_per_horse')}
                  value={costPerHorse()}
                />
              </Grid>
            </Grid>
          );
        })}

        <Button onClick={addHorse} variant="outlined" sx={{ mt: 2 }}>
          {t('horse_transport.add_horse')}
        </Button>
      </Box>

      <Button
        variant="contained"
        color="primary"
        onClick={handleSubmit}
        sx={{ mt: 4 }}
      >
        {isEdit ? t('horse_transport.update') : t('horse_transport.submit')}
      </Button>

      <Dialog open={driverDialogOpen} onClose={() => setDriverDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 900 }}>
          {t('horse_transport.add_driver')}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              autoFocus
              fullWidth
              label={t('horse_transport.driver_name')}
              value={driverForm.name}
              onChange={(e) => setDriverForm((prev) => ({ ...prev, name: e.target.value }))}
            />
            <TextField
              fullWidth
              label={t('horse_transport.driver_phone')}
              value={driverForm.phone}
              onChange={(e) => setDriverForm((prev) => ({ ...prev, phone: e.target.value }))}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDriverDialogOpen(false)}>
            {t('common.cancel') || 'Cancel'}
          </Button>
          <Button variant="contained" onClick={saveDriver}>
            {t('common.save') || 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default HorseTransportForm;
